/**
 * Workers: each one runs a plan for one stock — buys its lots, holds their targets, rests their
 * stops, follows its rules — and tells what happened. The list shows them at a glance; a worker's own
 * page shows the whole plan in plain rows: every lot with its entry, target and stop and where each
 * order stands, the figures, the rules, the orders at the broker, why it was opened, and the timeline.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowLeft, ChartLine, ChevronRight, MessagesSquare, Pencil, Play, Plus, Square } from 'lucide-react'
import { create } from 'zustand'
import { BROKER_LABEL } from '@shared/brokers'
import { DEFAULT_INSURANCE, stopPath, type BandSide, type Insurance, type InsuranceSetting, type PlanLot, type WorkerDetail, type WorkerOrderRecord, type WorkerRecord, type WorkerSpec } from '@shared/worker'
import { api, workersApi } from './api'
import type { Agent } from './data'
import { Steps } from './icons'
import { Btn, Card, Glyph, I, Status, Switch, Tabs, askSure, cx, signed, toneOf, usd, type PopEntry } from './kit'
import { Bar, Choose, Empty, Ladder, Page, Ticker, ask, qtyText, useTabs } from './Page'
import { useDemo } from './store'
import { Fig, Figures, GroupsTab, JournalTab, WatchersTab } from './WorkMore'

/* ── the workers, kept live for the bar and the page together ─────────── */

const useBook = create<{ list: WorkerRecord[] | null; id: string | null; one: WorkerDetail | null; error: string | null; editing: boolean; prices: Record<string, number>; chats: Record<string, string | null> }>(() => ({ list: null, id: null, one: null, error: null, editing: false, prices: {}, chats: {} }))
const text = (e: unknown, or: string) => (e instanceof Error ? e.message : or)
const loadList = () => workersApi.list().then((list) => useBook.setState({ list }), (e) => useBook.setState({ error: text(e, 'Could not load the workers') }))
const loadOne = (id: string) =>
  workersApi.get(id).then(
    (one) => useBook.getState().id === id && useBook.setState({ one, error: null }),
    (e) => useBook.getState().id === id && useBook.setState({ error: text(e, 'Could not load the worker') }),
  )
/** Which agent each conversation belongs to (a worker is minded by the agent it was planned with). */
const loadChats = () => api.listChats().then((all) => useBook.setState({ chats: Object.fromEntries(all.map((c) => [c.id, c.projectId])) }), () => {})

const st = () => useDemo.getState()
const say = (m: string) => st().say(m)
const fail = (e: unknown) => say(text(e, 'That did not work'))
const ic = (icon: typeof Plus) => <I icon={icon} className="text-ws-icon" />
const money = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(Math.abs(n))}`
const day = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
const at = (ms: number) => new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

const RUNNING = new Set(['waiting', 'working', 'open', 'closing', 'error'])
/** A change to a running worker is shown on that worker, not as a worker of its own. */
const own = (w: WorkerRecord) => !w.revises
const live = (w: WorkerRecord) => own(w) && RUNNING.has(w.status)
const draft = (w: WorkerRecord) => own(w) && w.status === 'draft'
const still = (w: WorkerRecord) => live(w) && (!!w.runtime?.attention || w.status === 'error')
const brokerOf = (w: WorkerRecord) => BROKER_LABEL[w.spec.broker ?? 'ibkr']

/** A worker's state, said plainly. */
export function workerState(w: WorkerRecord): { tone: 'free' | 'warn' | 'off' | 'down'; label: string } {
  if (still(w)) return { tone: 'down', label: 'Needs you' }
  switch (w.status) {
    case 'draft':
      return { tone: 'warn', label: 'Awaiting approval' }
    case 'waiting':
      return { tone: 'warn', label: 'Waiting to start' }
    case 'working':
      return { tone: 'warn', label: w.plan.band ? 'Watching its buy zone' : 'Waiting for a price' }
    case 'open':
      return { tone: 'free', label: 'Running' }
    case 'closing':
      return { tone: 'warn', label: 'Selling' }
    case 'done':
      return { tone: 'off', label: 'Finished' }
    case 'cancelled':
      return { tone: 'off', label: 'Stopped' }
    case 'rejected':
      return { tone: 'off', label: 'Rejected' }
    case 'expired':
      return { tone: 'off', label: 'Expired' }
    default:
      return { tone: 'off', label: 'Ended' }
  }
}

/** The figures of a worker as the list and the page show them, read from its plan and its live state. */
function figures(w: WorkerRecord, price?: number) {
  const lots = w.runtime?.lots ?? []
  const open = lots.reduce((a, x) => a + x.open, 0)
  const bought = lots.some((x) => x.entryFilled > 0) || !!w.spec.adopt
  const entry = w.runtime?.avgEntry ?? w.spec.adopt?.avgCost ?? w.plan.entryRef
  // the stops and targets as they stand now (a rule may have moved them), falling back to the plan
  const stops = w.plan.lots.filter((l) => !l.noStop).map((l) => lots.find((x) => x.lot === l.lot)?.stop ?? l.sl.stop).filter((v) => v > 0)
  const targets = w.plan.lots.map((l) => lots.find((x) => x.lot === l.lot)?.target ?? l.tp?.price ?? null).filter((v): v is number => v != null)
  const last = price ?? entry
  const realized = w.runtime?.realized ?? 0
  return { open, bought, entry, last, realized, stop: stops.length ? Math.min(...stops) : null, target: targets.length ? Math.min(...targets) : null, pnl: realized + (bought ? (last - entry) * open : 0) }
}
/** What every stop being hit now would come to: a loss still at risk, or a gain already locked in. */
function atStops(w: WorkerRecord): number {
  if (!w.runtime || w.status === 'waiting' || w.status === 'draft') return -(w.summary.maxLossNet ?? w.summary.maxLoss)
  const entry = w.runtime.avgEntry
  return w.runtime.lots.reduce((a, x) => {
    const plan = w.plan.lots.find((p) => p.lot === x.lot)
    if (plan?.noStop && !x.stop) return a
    const shares = x.open + (x.entryDone ? 0 : x.qty - x.entryFilled)
    const from = x.entryFilled > 0 ? (entry ?? plan?.entry.ref ?? w.plan.entryRef) : (plan?.entry.ref ?? w.plan.entryRef)
    return a + (x.stop - from) * shares
  }, 0)
}
const agentOf = (w: WorkerRecord, agents: Agent[], chats: Record<string, string | null>) => (w.chatId ? (agents.find((a) => a.id === chats[w.chatId!]) ?? agents.find((a) => a.kind === 'lead')) : undefined)

/* ── what can be done to one ─────────────────────────────────────────── */

const stopWorker = (w: WorkerRecord) =>
  askSure({
    title: `Stop ${w.name}?`,
    body: w.runtime?.lots.some((x) => x.open > 0) ? `Its orders are cancelled and what it holds is sold at the market. ${brokerOf(w)} fills it at the price of that moment.` : 'Its orders are cancelled. Nothing was bought, so nothing is sold.',
    cta: 'Stop worker',
    done: () => void workersApi.close(w.id).then(() => say(`${w.name} is stopping`), fail),
  })
const approve = (w: WorkerRecord) => void workersApi.approve(w.id, w.planHash).then(() => (useBook.setState({ editing: false }), say(w.plan.band ? 'Approved — the worker is watching its buy zone' : 'Approved — its orders are on their way')), fail)
const reject = (w: WorkerRecord) => askSure({ title: `Reject ${w.name}?`, body: `The plan is dropped. Nothing was sent to ${brokerOf(w)}.`, cta: 'Reject', done: () => void workersApi.reject(w.id).then(() => useBook.setState({ editing: false }), fail) })
const resume = (w: WorkerRecord) => void workersApi.resume(w.id).then(() => say(`${w.name} goes on`), fail)
/** Draw a draft again from a changed plan, in the conversation it came from (the agent that minds it stays the same). */
const redraw = async (spec: WorkerSpec, chatId: string | null): Promise<WorkerDetail> => {
  const r = await fetch('/api/workers/draft', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ spec, chatId }) })
  const body = (await r.json().catch(() => ({}))) as WorkerDetail & { errors?: string[]; error?: string }
  if (!r.ok) throw new Error(body.errors?.join(' · ') ?? body.error ?? 'The plan could not be drawn')
  return body
}
const replan = (w: WorkerRecord, by?: Agent) => ask(by?.id, `${w.name} planını yeniden kuralım: `)

function workerMenu(id: string): PopEntry[] {
  const b = useBook.getState()
  const s = st()
  const w = b.list?.find((x) => x.id === id)
  if (!w) return []
  return [
    { label: 'Open', onSelect: () => s.set({ workerId: id }) },
    { label: 'Open chart', icon: ic(ChartLine), onSelect: () => s.openStock(w.symbol) },
    { label: 'Re-plan in chat', icon: ic(MessagesSquare), onSelect: () => replan(w, agentOf(w, s.agents, b.chats)) },
    ...(live(w) || draft(w) ? [{ label: 'Edit by hand', icon: ic(Pencil), onSelect: () => (s.set({ workerId: id }), useBook.setState({ editing: true })) }] : []),
    ...(still(w) ? [{ label: 'Resume', icon: ic(Play), onSelect: () => resume(w) }] : []),
    ...(live(w) ? ['rule' as const, { label: 'Stop worker', icon: <I icon={Square} />, danger: true, onSelect: () => stopWorker(w) }] : []),
  ]
}

export function WorkersBar() {
  const t = useTabs()
  const list = useBook((s) => s.list) ?? []
  const one = useBook((s) => s.one)
  const editing = useBook((s) => s.editing)
  const chats = useBook((s) => s.chats)
  const agents = useDemo((s) => s.agents)
  const workerId = useDemo((s) => s.workerId)
  const setS = useDemo((s) => s.set)
  const openStock = useDemo((s) => s.openStock)
  const open = workerId ? (one?.id === workerId ? one : list.find((w) => w.id === workerId)) : undefined
  if (workerId) {
    return (
      <Bar
        right={
          open && (
            <>
              <Btn icon={ChartLine} iconSize={15} onClick={() => openStock(open.symbol)}>
                <span className="hidden md:inline">Chart</span>
              </Btn>
              <Btn icon={MessagesSquare} iconSize={15} className="ml-2" onClick={() => replan(open, agentOf(open, agents, chats))}>
                <span className="hidden md:inline">Re-plan in chat</span>
              </Btn>
              {draft(open) && (
                <>
                  <Btn icon={Pencil} iconSize={15} className={cx('ml-2', editing && 'bg-ws-panel text-ws-fg')} onClick={() => useBook.setState({ editing: !editing })}>
                    <span className="hidden md:inline">Edit</span>
                  </Btn>
                  <Btn variant="outline" className="ml-3" onClick={() => reject(open)}>
                    Reject
                  </Btn>
                  <Btn variant="white" className="ml-2" title="Approval is bound to exactly this plan" onClick={() => approve(open)}>
                    Approve
                  </Btn>
                </>
              )}
              {live(open) && (
                <>
                  <Btn icon={Pencil} iconSize={15} className={cx('ml-2', editing && 'bg-ws-panel text-ws-fg')} onClick={() => useBook.setState({ editing: !editing })}>
                    <span className="hidden md:inline">Edit</span>
                  </Btn>
                  {still(open) && (
                    <Btn icon={Play} iconSize={15} className="ml-2" onClick={() => resume(open)}>
                      <span className="hidden md:inline">Resume</span>
                    </Btn>
                  )}
                  <Btn variant="danger" className="ml-3" onClick={() => stopWorker(open)}>
                    Stop
                  </Btn>
                </>
              )}
            </>
          )
        }
      >
        <Btn icon={ArrowLeft} onClick={() => (useBook.setState({ editing: false }), setS({ workerId: null }))}>
          Workers
        </Btn>
      </Bar>
    )
  }
  return (
    <Bar
      right={
        <Btn icon={Plus} onClick={() => ask(null, 'Bana bir worker planı hazırla: ')}>
          <span className="hidden sm:inline">New worker</span>
        </Btn>
      }
    >
      <Tabs
        options={[
          { id: 'running', label: 'Running', count: list.filter(live).length },
          { id: 'drafts', label: 'Awaiting approval', count: list.filter((w) => w.status === 'draft').length || undefined },
          { id: 'history', label: 'History' },
          { id: 'groups', label: 'Groups' },
          { id: 'watchers', label: 'Watchers & scans' },
          { id: 'journal', label: 'Journal' },
        ]}
        value={t.workers}
        onChange={(v) => t.set({ workers: v })}
      />
    </Bar>
  )
}

/* ── the list ────────────────────────────────────────────────────────── */

function List({ workers }: { workers: WorkerRecord[] }) {
  const t = useTabs()
  const agents = useDemo((s) => s.agents)
  const setS = useDemo((s) => s.set)
  const go = useDemo((s) => s.go)
  const prices = useBook((s) => s.prices)
  const chats = useBook((s) => s.chats)
  const tab = ['running', 'drafts', 'history', 'groups', 'watchers', 'journal'].includes(t.workers) ? t.workers : 'running'
  // a change waiting for approval is shown as the worker it changes
  const changing = new Set(workers.filter((w) => w.revises && w.status === 'draft').map((w) => w.revises!))
  const list = workers.filter((w) => (tab === 'running' ? live(w) : tab === 'drafts' ? draft(w) || changing.has(w.id) : own(w) && !live(w) && !draft(w)))
  // what is at risk if every stop is hit now, what has been made, and what stands still
  const risk = workers.filter(live).reduce((a, w) => a + Math.min(0, atStops(w)), 0)
  const realized = workers.filter(own).reduce((a, w) => a + (w.runtime?.realized ?? 0), 0)
  const waiting = workers.filter((w) => w.status === 'draft').length
  const stuck = workers.filter(still).length
  return (
    <Page className="mx-auto max-w-[1360px]">
      <Figures cols="sm:grid-cols-3">
        <Fig label="Open risk" value={money(risk)} sub="at current stops" cls={risk < 0 ? 'text-ws-down' : 'text-ws-fg'} />
        <Fig label="Realized" value={money(realized)} sub="all workers" cls={toneOf(realized) === 'text-ws-fg-3' ? 'text-ws-fg' : toneOf(realized)} />
        <Fig label="Needs attention" value={stuck + waiting} sub={stuck ? 'a worker stands still and waits for you' : waiting ? 'a plan waits for your approval' : 'all clear'} />
      </Figures>
      <div className="mt-2 flex justify-end">
        <button type="button" onClick={() => (go('settings'), useTabs.getState().set({ settings: 'trading' }))} className="rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg">
          Risk limits are in Settings
        </button>
      </div>

      <div className="mt-4">
        {tab === 'groups' ? (
          <GroupsTab workers={workers} />
        ) : tab === 'watchers' ? (
          <WatchersTab />
        ) : tab === 'journal' ? (
          <JournalTab />
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {list.map((w) => {
                const by = agentOf(w, agents, chats)
                const f = figures(w, prices[w.symbol])
                const state = changing.has(w.id) && tab === 'drafts' ? { tone: 'warn' as const, label: 'A change waits for you' } : workerState(w)
                return (
                  <Card key={w.id} onClick={() => setS({ workerId: w.id })} menu={() => workerMenu(w.id)} className="cursor-pointer px-3 pb-3 pt-[10px]">
                    <div className="flex h-7 items-center">
                      <Ticker symbol={w.symbol} />
                      <span className="ml-3 min-w-0 truncate font-medium text-ws-fg">{w.name}</span>
                      <Status tone={state.tone} className="ml-auto pl-2">
                        {state.label}
                      </Status>
                    </div>
                    <div className="mt-[10px] text-[12px] leading-[15px] tracking-normal text-ws-fg-3">
                      {qtyText(w.plan.quantity)} {w.plan.quantity === 1 ? 'share' : 'shares'} · {w.plan.lots.length} {w.plan.lots.length === 1 ? 'lot' : 'lots'} · {draft(w) ? 'drafted' : 'since'} {day(w.approvedAt ?? w.createdAt)}
                    </div>
                    <div className="mt-4">
                      <Ladder stop={f.stop} entry={f.entry} last={f.last} target={f.target} />
                    </div>
                    <div className="mt-3 flex h-5 items-center gap-[6px] border-t border-ws-edge pt-3 text-[12px] tracking-normal text-ws-fg-3">
                      {by && <Glyph palette={by.palette} size={14} />}
                      {by?.name}
                      <span className="text-ws-fg-4">{by ? '· ' : ''}{brokerOf(w)}</span>
                      {f.bought && !draft(w) && <span className={cx('ml-auto tabular-nums', toneOf(f.pnl))}>{signed(f.pnl)}</span>}
                    </div>
                  </Card>
                )
              })}
            </div>
            {!list.length && <p className="m-0 text-[12px] tracking-normal text-ws-fg-3">{tab === 'running' ? 'No running workers.' : tab === 'drafts' ? 'Nothing is waiting for your approval.' : 'No finished workers yet.'}</p>}
          </>
        )}
      </div>
    </Page>
  )
}

/* ── one worker ──────────────────────────────────────────────────────── */

interface Leg {
  tone: 'free' | 'warn' | 'off' | 'down'
  text: string
  gone?: boolean
}
const DEAD = new Set(['Cancelled', 'ApiCancelled', 'Inactive'])
/** Where an order of the plan stands: at the broker, held here, filled, cancelled — or not sent yet. */
function legOf(w: WorkerDetail, lot: PlanLot, role: 'entry' | 'tp' | 'sl'): Leg {
  const at = brokerOf(w)
  const rows = w.orders.filter((o) => o.lot === lot.lot && o.role === role)
  const o = rows.filter((x) => !DEAD.has(x.status) && x.status !== 'Filled').at(-1) ?? rows.at(-1)
  const rs = w.runtime?.lots.find((x) => x.lot === lot.lot)
  if (w.status === 'draft') return { tone: 'off', text: role === 'tp' && w.spec.broker === 'robinhood' ? 'Watched here on approval' : 'Sent on approval' }
  if (role === 'entry' && (w.spec.adopt || rs?.entryDone)) return { tone: 'free', text: w.spec.adopt ? 'Already held' : 'Filled' }
  if (role === 'tp' && rs && rs.tpFilled > 0 && rs.open === 0) return { tone: 'free', text: 'Filled' }
  if (role === 'sl' && rs && rs.slFilled > 0 && rs.open === 0) return { tone: 'free', text: 'Filled' }
  if (!o) {
    if (!RUNNING.has(w.status)) return { tone: 'off', text: 'Not sent', gone: true }
    if (role === 'entry') return { tone: 'warn', text: w.status === 'waiting' ? 'Waiting to start' : 'Waiting for the price' }
    if (role === 'sl' && lot.noStop) return { tone: 'off', text: 'No stop' }
    return { tone: 'off', text: role === 'tp' && w.spec.broker === 'robinhood' ? 'Watched here' : 'Held by the worker' }
  }
  if (o.status === 'Filled' || (o.filled > 0 && o.filled >= o.quantity)) return { tone: 'free', text: 'Filled' }
  if (o.status === 'Unknown') return { tone: 'down', text: `Not found at ${at}` }
  if (DEAD.has(o.status)) return o.filled > 0 ? { tone: 'warn', text: `Partly filled, ${qtyText(o.filled)} of ${qtyText(o.quantity)}` } : { tone: 'off', text: 'Cancelled', gone: true }
  if (o.filled > 0) return { tone: 'warn', text: `Partly filled, ${qtyText(o.filled)} of ${qtyText(o.quantity)}` }
  if (o.status === 'PreSubmitted' || o.status === 'ApiPending' || o.status === 'PendingSubmit') return { tone: 'off', text: role === 'entry' ? `Held at ${at}` : `At ${at}, waits for its entry` }
  return { tone: 'free', text: `At ${at}` }
}
const State = ({ leg }: { leg: Leg }) => (
  <Status tone={leg.tone} className={leg.gone ? 'text-ws-fg-4' : undefined}>
    {leg.text}
  </Status>
)
const orderKind = (type: string, limit: number | null, stop: number | null) => (type === 'MKT' ? 'market' : type === 'LMT' ? `limit ${limit?.toFixed(2) ?? ''}` : type === 'STP' ? `stop ${stop?.toFixed(2) ?? ''}` : type === 'STP LMT' ? `stop ${stop?.toFixed(2) ?? ''} limit ${limit?.toFixed(2) ?? ''}` : type.toLowerCase())
export const entryText = (w: WorkerRecord, lot?: PlanLot) => (w.spec.adopt ? 'already held' : w.plan.band ? `inside ${w.plan.band.min.toFixed(2)}–${w.plan.band.max.toFixed(2)}` : orderKind((lot ?? w.plan.lots[0]).entry.orderType, (lot ?? w.plan.lots[0]).entry.limitPrice, (lot ?? w.plan.lots[0]).entry.stopPrice))

/** Every level of the plan on one line: its stops, its entry, each target, and where the price is now. */
function Levels({ stops, targets, entry, last }: { stops: number[]; targets: number[]; entry: number; last: number }) {
  const all = [...stops, entry, last, ...targets]
  const lo = Math.min(...all)
  const hi = Math.max(...all)
  const pad = (hi - lo || 1) * 0.05
  const pos = (v: number) => `${(((v - (lo - pad)) / (hi - lo + pad * 2)) * 100).toFixed(2)}%`
  const up = last >= entry
  const marks = [...stops.map((v, i) => ({ k: stops.length > 1 ? `Stop ${i + 1}` : 'Stop', v, cls: 'text-ws-down' })), { k: 'Entry', v: entry, cls: 'text-ws-fg-2' }, { k: 'Last', v: last, cls: 'text-ws-fg' }, ...targets.map((v, i) => ({ k: targets.length > 1 ? `Target ${i + 1}` : 'Target', v, cls: 'text-ws-up' }))].sort((a, b) => a.v - b.v)
  return (
    <div>
      <div className="relative h-4">
        <span className="absolute inset-x-0 top-[7px] h-[2px] bg-ws-edge" />
        <span className={cx('absolute top-[7px] h-[2px]', up ? 'bg-ws-up' : 'bg-ws-down')} style={{ left: pos(Math.min(entry, last)), right: `calc(100% - ${pos(Math.max(entry, last))})` }} />
        {stops.map((v) => (
          <span key={`s${v}`} className="absolute top-[3px] h-[10px] w-[2px] -translate-x-px bg-ws-down" style={{ left: pos(v) }} />
        ))}
        <span className="absolute top-[3px] h-[10px] w-[2px] -translate-x-px bg-ws-fg-3" style={{ left: pos(entry) }} />
        {targets.map((v, i) => (
          <span key={`t${i}`} className="absolute top-[3px] h-[10px] w-[2px] -translate-x-px bg-ws-up" style={{ left: pos(v) }} />
        ))}
        <span className="absolute top-[4px] h-2 w-2 -translate-x-1/2 rounded-full bg-white shadow-[0_0_0_2px_var(--color-ws-bg)]" style={{ left: pos(last) }} />
      </div>
      <dl className="m-0 mt-2 flex justify-between gap-3">
        {marks.map((m, i) => (
          <div key={m.k} className={i === 0 ? '' : i === marks.length - 1 ? 'text-right' : 'text-center'}>
            <dt className="text-[11px] leading-4 tracking-normal text-ws-fg-4">{m.k}</dt>
            <dd className={cx('m-0 text-[12px] leading-4 tracking-normal tabular-nums', m.cls)}>{m.v.toFixed(2)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

const head = 'flex h-10 items-center gap-3 border-b border-ws-edge text-ws-fg'
const field = 'h-7 w-[88px] rounded-[6px] border border-ws-line bg-ws-field px-2 text-right text-[13px] tracking-normal tabular-nums transition-colors duration-150 focus:border-ws-focus'
const num = (v: string) => {
  const n = Number(v.replace(',', '.').trim())
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
}
const px = (n?: number | null) => (n == null ? '' : n.toFixed(2))
const SIDES: readonly (readonly [BandSide, string])[] = [
  ['wait', 'wait'],
  ['notify', 'tell me'],
  ['skipDay', 'skip the day'],
  ['pause', 'pause'],
  ['cancel', 'cancel'],
]
const sideText = (s: BandSide) => SIDES.find((x) => x[0] === s)?.[1] ?? s
type Sure = '50' | '70' | '80' | '90'

/** What is being typed while a plan is edited by hand. Targets and stops are the plan's tiers: lots that share one move together. */
interface Draft {
  targets: string[]
  stops: string[]
  entry: string
  lo: string
  hi: string
  thesis: string
  noChase: string
  sessions: string
  under: BandSide
  over: BandSide
  breakEven: boolean
  protect: boolean
  stall: boolean
  confidence: Sure
  runner: boolean
  runnerTarget: string
  keep: boolean[]
}
const insuranceOf = (w: WorkerRecord): InsuranceSetting => ({ ...DEFAULT_INSURANCE, ...(w.plan.insurance ?? { on: false, protectOthers: false }) })
const hasRunner = (w: WorkerRecord) => w.plan.tpShares.reduce((a, b) => a + b, 0) < w.plan.quantity
function draftOf(w: WorkerRecord): Draft {
  const z = w.spec.entry.band
  const ins = insuranceOf(w)
  return {
    targets: w.spec.takeProfits.map((t) => px(t.price)),
    stops: w.spec.stopLosses.map((s) => px(s.price)),
    entry: px(w.spec.entry.limitPrice ?? w.spec.entry.stopPrice),
    lo: px(z?.min),
    hi: px(z?.max),
    thesis: px(z?.invalid),
    noChase: px(z?.chase),
    sessions: z?.window?.kind === 'sessions' ? `${z.window.count}` : '',
    under: z?.below ?? w.plan.band?.below ?? 'notify',
    over: z?.above ?? w.plan.band?.above ?? 'notify',
    breakEven: ins.on,
    protect: ins.protectOthers,
    stall: !!ins.stall?.on,
    confidence: `${ins.stall?.confidence === 60 ? 70 : (ins.stall?.confidence ?? 80)}` as Sure,
    runner: hasRunner(w),
    runnerTarget: '',
    keep: w.spec.rules.map(() => true),
  }
}
/** The insurance as a plan writes it (what is not set is left out, never null). */
function toSpec(ins: InsuranceSetting): Insurance {
  const s = ins.stall
  return { on: ins.on, level: ins.level, basis: ins.basis, protectOthers: ins.protectOthers, ...(s ? { stall: { on: s.on, sigmas: s.sigmas, confidence: s.confidence, ...(s.way != null ? { way: s.way } : {}), ...(s.minutes != null ? { minutes: s.minutes } : {}) } } : {}) }
}

/** A row of the plan's settings: what it is, and its value (or the field to change it). */
const Line = ({ label, sub, children }: { label: string; sub?: string; children: ReactNode }) => (
  <div className="flex min-h-10 items-center justify-between gap-4 py-[6px] text-[13px] tracking-normal">
    <span className="min-w-0">
      <span className="block text-ws-fg-2">{label}</span>
      {sub && <span className="block text-[12px] leading-4 text-ws-fg-4">{sub}</span>}
    </span>
    <span className="flex shrink-0 items-center gap-2 tabular-nums text-ws-fg">{children}</span>
  </div>
)

function Detail({ w, change }: { w: WorkerDetail; change: WorkerRecord | undefined }) {
  const agents = useDemo((s) => s.agents)
  const openChat = useDemo((s) => s.openChat)
  const setS = useDemo((s) => s.set)
  const chats = useBook((s) => s.chats)
  const price = useBook((s) => s.prices[w.symbol])
  const editing = useBook((s) => s.editing) && (live(w) || draft(w))
  const [orders, setOrders] = useState(false)
  const [d, setD] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [all, setAll] = useState(false)
  useEffect(() => {
    setD(editing ? draftOf(w) : null)
  }, [editing, w.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const by = agentOf(w, agents, chats)
  const plan = w.plan
  const spec = w.spec
  const f = figures(w, price)
  const total = plan.quantity
  const bought = f.bought
  const zone = plan.band
  const ins = insuranceOf(w)
  const runner = hasRunner(w)
  const entryOpen = !bought && spec.entry.orderType !== 'MKT' && !zone
  const zoneOpen = !!spec.entry.band && !bought
  const rs = (lot: number) => w.runtime?.lots.find((x) => x.lot === lot)
  const stopNow = (l: PlanLot) => rs(l.lot)?.stop ?? l.sl.stop
  const targetNow = (l: PlanLot) => rs(l.lot)?.target ?? l.tp?.price ?? null
  const stops = [...new Set(plan.lots.filter((l) => !(l.noStop && !stopNow(l))).map(stopNow))].filter((v) => v > 0)
  const targets = plan.lots.map(targetNow).filter((v): v is number => v != null)
  const lockedIn = atStops(w)
  const fig = (label: string, value: string, cls = 'text-ws-fg') => (
    <div className="px-3 py-3 first:pl-0 max-sm:odd:pl-0">
      <dt className="text-[12px] leading-4 tracking-normal text-ws-fg-3">{label}</dt>
      <dd className={cx('m-0 mt-1 text-[18px] font-medium leading-6 tracking-[-0.015em] tabular-nums', cls)}>{value}</dd>
    </div>
  )
  // every order the plan stands for, as the broker knows it (or as the plan will send it)
  const book = w.orders.length
    ? w.orders.map((o: WorkerOrderRecord) => ({ tag: o.planKey, what: `${o.action === 'BUY' ? 'Buy' : 'Sell'} ${qtyText(o.quantity)}`, kind: orderKind(o.orderType, o.limitPrice, o.stopPrice), state: (o.status === 'Filled' ? { tone: 'free', text: 'Filled' } : DEAD.has(o.status) ? { tone: 'off', text: 'Cancelled', gone: true } : o.status === 'Unknown' ? { tone: 'down', text: `Not found at ${brokerOf(w)}` } : { tone: 'free', text: `At ${brokerOf(w)}` }) as Leg, id: `#${o.orderId}` }))
    : plan.orders.map((o) => ({ tag: o.key, what: `${o.action === 'BUY' ? 'Buy' : 'Sell'} ${qtyText(o.qty)}`, kind: orderKind(o.orderType, o.limitPrice, o.stopPrice), state: { tone: 'off', text: draft(w) ? 'Sent on approval' : 'Not sent', gone: !draft(w) } as Leg, id: '—' }))
  const fired = new Set(w.runtime?.firedRules ?? [])
  const moves = (l: PlanLot) => stopPath(spec, l, plan).filter((s) => !fired.has(s.rule) && !(typeof s.to === 'number' && stopNow(l) >= s.to))

  /* what the hand-made change comes to: each thing that differs, and whether it can be sent */
  const problems: string[] = []
  const diffs: string[] = []
  if (d) {
    if (d.stops.some((x) => !num(x))) problems.push('Every stop needs a price.')
    if (d.targets.some((x) => !num(x))) problems.push('Every target needs a price.')
    if (entryOpen && !num(d.entry)) problems.push('The entry needs a price.')
    if (zoneOpen && !(num(d.lo) && num(d.hi))) problems.push('The buy zone needs both prices.')
    if (zoneOpen && d.sessions.trim() && !(Math.round(Number(d.sessions)) >= 1 && Math.round(Number(d.sessions)) <= 60)) problems.push('Sessions to watch: 1 to 60.')
    if (runner && !d.runner && !num(d.runnerTarget)) problems.push('To switch the runner off, give its shares a target.')
    spec.takeProfits.forEach((t, i) => num(d.targets[i]) && num(d.targets[i]) !== t.price && diffs.push(`Target ${i + 1} ${t.price.toFixed(2)} → ${num(d.targets[i])!.toFixed(2)}`))
    spec.stopLosses.forEach((s, i) => num(d.stops[i]) && num(d.stops[i]) !== s.price && diffs.push(`Stop ${i + 1} ${s.price.toFixed(2)} → ${num(d.stops[i])!.toFixed(2)}`))
    const entry0 = spec.entry.limitPrice ?? spec.entry.stopPrice
    if (entryOpen && num(d.entry) && num(d.entry) !== entry0) diffs.push(`Entry ${px(entry0)} → ${num(d.entry)!.toFixed(2)}`)
    if (zoneOpen) {
      const z = spec.entry.band!
      if (num(d.lo) && num(d.hi) && (num(d.lo) !== z.min || num(d.hi) !== z.max)) diffs.push(`Buy zone ${z.min.toFixed(2)}–${z.max.toFixed(2)} → ${num(d.lo)!.toFixed(2)}–${num(d.hi)!.toFixed(2)}`)
      const lvl = (name: string, was: number | undefined, now: string) => (num(now) ?? undefined) !== was && diffs.push(`${name} ${was?.toFixed(2) ?? 'none'} → ${num(now)?.toFixed(2) ?? 'none'}`)
      lvl('Thesis level', z.invalid, d.thesis)
      lvl('No-chase level', z.chase, d.noChase)
      const was = z.window?.kind === 'sessions' ? z.window.count : undefined
      if ((Math.round(Number(d.sessions)) || undefined) !== was) diffs.push(`Sessions to watch ${was ?? 'as set'} → ${Math.round(Number(d.sessions)) || 'as set'}`)
      if (d.under !== (z.below ?? zone?.below ?? 'notify')) diffs.push(`Under the zone: ${sideText(z.below ?? zone?.below ?? 'notify')} → ${sideText(d.under)}`)
      if (d.over !== (z.above ?? zone?.above ?? 'notify')) diffs.push(`Over the zone: ${sideText(z.above ?? zone?.above ?? 'notify')} → ${sideText(d.over)}`)
    }
    const sw = (name: string, was: boolean, now: boolean) => was !== now && diffs.push(`${name}: ${was ? 'on' : 'off'} → ${now ? 'on' : 'off'}`)
    sw('Break-even step', ins.on, d.breakEven)
    sw('Protect the others after a profit', ins.protectOthers, d.protect)
    sw('Sell when it stalls near the target', !!ins.stall?.on, d.stall)
    if (d.stall && Number(d.confidence) !== (ins.stall?.confidence ?? 80)) diffs.push(`Stall: sure to ${ins.stall?.confidence ?? 80}% → ${d.confidence}%`)
    sw('Runner (the last shares ride with their stop)', runner, d.runner)
    if (runner && d.runner && num(d.runnerTarget)) diffs.push(`Runner target none → ${num(d.runnerTarget)!.toFixed(2)}`)
    spec.rules.forEach((r, i) => !d.keep[i] && diffs.push(`Rule ${r.id} removed`))
  }
  /** The plan as the fields now say it. */
  const next = (): WorkerSpec | null => {
    if (!d || problems.length) return null
    let tp = spec.takeProfits.map((t, i) => ({ ...t, price: num(d.targets[i])! }))
    if (!runner && d.runner && tp.length) tp = tp.slice(0, -1)
    if (runner && num(d.runnerTarget)) tp = [...tp, { price: num(d.runnerTarget)!, qty: { kind: 'rest' as const } }]
    const e = { ...spec.entry }
    if (zoneOpen) {
      const { invalid: _i, chase: _c, ...rest } = spec.entry.band!
      const count = Math.round(Number(d.sessions)) || null
      e.band = { ...rest, min: num(d.lo)!, max: num(d.hi)!, below: d.under, above: d.over, ...(count ? { window: { kind: 'sessions' as const, count } } : {}), ...(num(d.thesis) ? { invalid: num(d.thesis)! } : {}), ...(num(d.noChase) ? { chase: num(d.noChase)! } : {}) }
      delete e.limitPrice
    }
    if (entryOpen) {
      if (e.orderType === 'STP') e.stopPrice = num(d.entry)!
      else e.limitPrice = num(d.entry)!
    }
    const stall = { ...DEFAULT_INSURANCE.stall!, ...(ins.stall ?? {}), on: d.stall, confidence: Number(d.confidence) as 50 | 70 | 80 | 90 }
    return { ...spec, entry: e, takeProfits: tp, stopLosses: spec.stopLosses.map((s, i) => ({ ...s, price: num(d.stops[i])! })), insurance: toSpec({ ...ins, on: d.breakEven, protectOthers: d.protect, stall }), rules: spec.rules.filter((_, i) => d.keep[i]) }
  }
  /** Ask the server what the change comes to. A running worker gets a change to approve; a draft is drawn again. */
  const show = async () => {
    const plan2 = next()
    if (!plan2) return
    setBusy(true)
    try {
      if (draft(w)) {
        const fresh = await redraw(plan2, w.chatId)
        await workersApi.reject(w.id).catch(() => undefined)
        useBook.setState({ editing: false })
        setS({ workerId: fresh.id })
        say('The draft is changed — it still waits for your approval')
      } else {
        await workersApi.revise(w.id, plan2)
        useBook.setState({ editing: false })
        window.scrollTo({ top: 0 })
      }
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }
  const set = (p: Partial<Draft>) => setD((x) => (x ? { ...x, ...p } : x))
  const put = (list: string[], i: number, v: string) => list.map((x, k) => (k === i ? v : x))
  const cols = 'grid-cols-[36px_minmax(150px,1.2fr)_minmax(130px,1fr)_minmax(190px,1.5fr)_64px]'
  const off = <span className="text-ws-fg-4">none</span>
  const state = workerState(w)
  const checks = w.checks.filter((c) => c.level !== 'info')
  const mind = [...w.checks.filter((c) => c.level === 'info').map((c) => `${c.label} — ${c.detail}`), ...w.summary.warnings, ...(w.summary.basis?.warnings ?? []), ...(w.summary.noteIssues ?? []), ...(w.preview?.warning ? [w.preview.warning] : [])]
  const events = all ? w.events : w.events.slice(0, 40)
  return (
    <Page className="mx-auto max-w-[1360px]">
      <div className="grid items-start gap-x-8 gap-y-8 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <div className="flex min-h-7 flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="m-0 text-[20px] font-medium leading-7 tracking-[-0.015em] text-ws-fg">{w.name}</h1>
            <Status tone={state.tone}>{state.label}</Status>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-2 text-[13px] leading-5 tracking-normal text-ws-fg-2">
            <span>
              {/* (said as buy and sell: there is no leverage and no short selling here) */}
              {spec.adopt ? `Manages ${qtyText(total)} ${w.symbol} you already hold` : `Buy ${qtyText(total)} ${w.symbol} · ${entryText(w)}`} · {plan.lots.length} {plan.lots.length === 1 ? 'lot' : 'lots'}
            </span>
            <span className="text-ws-fg-4">
              {brokerOf(w)}
              {w.account ? ` ··${w.account.slice(-4)}` : ''} · plan v{w.version ?? 1}
            </span>
          </div>

          {/* it stands still: why, and the way on */}
          {(w.runtime?.attention || w.error) && live(w) && (
            <Card className="mt-5 px-3 pb-3 pt-[10px]">
              <div className="flex h-5 items-center text-[12px] tracking-normal text-ws-fg-3">
                It stands still
                <Status tone="down" className="ml-auto">
                  Needs you
                </Status>
              </div>
              <p className="m-0 mt-2 text-[13px] leading-5 tracking-normal text-ws-fg">{w.runtime?.attention ?? w.error}</p>
              <p className="m-0 mt-2 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">Its orders at {brokerOf(w)} stay where they are. Its automatic rules do nothing until you let it go on.</p>
              <div className="mt-3 flex gap-2">
                <Btn variant="white" onClick={() => resume(w)}>
                  Resume
                </Btn>
                <Btn variant="outline" onPanel onClick={() => replan(w, by)}>
                  Ask in chat
                </Btn>
              </div>
            </Card>
          )}

          {/* a change, shown before anything happens: approve it or drop it */}
          {change && (
            <Card className="mt-5 px-3 pb-3 pt-[10px]">
              <div className="flex h-5 items-center text-[12px] tracking-normal text-ws-fg-3">
                Change to a running worker
                <Status tone="warn" className="ml-auto">
                  Waiting for you
                </Status>
              </div>
              <ul className="m-0 mt-2 list-none divide-y divide-ws-edge border-y border-ws-edge p-0">
                {(change.summary.revision?.lines ?? [change.summary.headline]).map((x) => (
                  <li key={x} className="py-2 text-[13px] leading-5 tracking-normal tabular-nums text-ws-fg">
                    {x}
                  </li>
                ))}
                {[...(change.summary.revision?.warnings ?? []), ...change.checks.filter((c) => !c.ok && c.level !== 'info').map((c) => `${c.label} — ${c.detail}`)].map((x) => (
                  <li key={x} className="flex gap-3 py-2 text-[13px] leading-5 tracking-normal text-ws-fg-2">
                    <span className="mt-[7px] block h-[6px] w-[6px] shrink-0 rounded-full bg-ws-warn" />
                    {x}
                  </li>
                ))}
              </ul>
              <p className="m-0 mt-2 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">Nothing changes until you approve. The orders at {brokerOf(w)} are replaced to match.</p>
              <div className="mt-3 flex gap-2">
                <Btn variant="white" onClick={() => void workersApi.approve(change.id, change.planHash).then(() => say('Change approved — the worker runs the new plan'), fail)}>
                  Approve change
                </Btn>
                <Btn variant="outline" onPanel onClick={() => void workersApi.reject(change.id).then(() => say('The change is dropped — the plan stays as it was'), fail)}>
                  Drop it
                </Btn>
              </div>
            </Card>
          )}

          <dl className="m-0 mt-5 grid grid-cols-2 border-y border-ws-edge sm:grid-cols-4 sm:divide-x sm:divide-ws-edge">
            {fig('Realized', money(f.realized), f.realized ? toneOf(f.realized) : 'text-ws-fg')}
            {fig('Open shares', `${qtyText(Number(f.open.toFixed(6)))} / ${qtyText(total)}`)}
            {fig('Average entry', bought ? f.entry.toFixed(2) : '—')}
            {live(w) || draft(w) ? fig(lockedIn >= 0 ? 'Locked in at the stops' : 'Planned max loss', money(lockedIn), toneOf(lockedIn)) : fig('Result', money(f.realized), toneOf(f.realized))}
          </dl>

          <div className="mt-6">
            <Levels stops={stops} targets={targets} entry={f.entry} last={f.last} />
          </div>

          {/* the plan, lot by lot: what is bought, where it is sold, and where each order stands */}
          <div className={cx(head, 'mt-8')}>
            The plan
            <span className="text-[12px] tracking-normal text-ws-fg-3">{w.approvedAt ? `approved ${at(w.approvedAt)}` : 'not approved yet'}</span>
          </div>
          <div className="ws-noscroll overflow-x-auto">
            <div className="min-w-[620px]">
              <div className={cx('grid h-8 items-center gap-3 border-b border-ws-edge text-[12px] tracking-normal text-ws-fg-3', cols)}>
                <span>Lot</span>
                <span>Entry</span>
                <span>Target</span>
                <span>Stop</span>
                <span className="text-right">Filled</span>
              </div>
              <div className="divide-y divide-ws-edge">
                {plan.lots.map((l) => {
                  const tp = legOf(w, l, 'tp')
                  const sl = legOf(w, l, 'sl')
                  const r = rs(l.lot)
                  const left = !r || r.open > 0 || !r.entryDone // (not sold out yet: its stop can still be moved)
                  const ti = l.tp ? l.tp.tier - 1 : -1
                  const si = l.sl.tier - 1
                  return (
                    <div key={l.lot} className={cx('grid items-start gap-3 py-[10px] text-[13px] leading-5 tracking-normal', cols)}>
                      <span className="text-ws-fg-3">{l.lot}</span>
                      <span>
                        <span className="block tabular-nums text-ws-fg">
                          Buy {qtyText(l.qty)} <span className="text-ws-fg-3">· {entryText(w, l)}</span>
                        </span>
                        <State leg={legOf(w, l, 'entry')} />
                      </span>
                      <span>
                        {d && l.tp && tp.text !== 'Filled' && d.targets[ti] != null ? (
                          <input value={d.targets[ti]} onChange={(e) => set({ targets: put(d.targets, ti, e.target.value) })} inputMode="decimal" aria-label={`Target of lot ${l.lot}`} className={cx(field.replace('text-right', 'text-left'), 'mb-1 block')} />
                        ) : (
                          <span className="block tabular-nums text-ws-fg">{targetNow(l)?.toFixed(2) ?? <span className="text-ws-fg-3">rides with its stop</span>}</span>
                        )}
                        {l.tp ? <State leg={tp} /> : <Status tone="off">Runner</Status>}
                      </span>
                      <span>
                        {d && left && !l.noStop && d.stops[si] != null ? (
                          <input value={d.stops[si]} onChange={(e) => set({ stops: put(d.stops, si, e.target.value) })} inputMode="decimal" aria-label={`Stop of lot ${l.lot}`} className={cx(field.replace('text-right', 'text-left'), 'mb-1 block')} />
                        ) : (
                          <span className="block tabular-nums text-ws-fg">
                            {l.noStop && !stopNow(l) ? <span className="text-ws-fg-3">none</span> : stopNow(l).toFixed(2)}
                            {moves(l).map((m) => (
                              <span key={m.rule} className="text-ws-fg-3" title={m.after}>
                                {' '}
                                → {m.to === 'breakeven' ? 'break-even' : typeof m.to === 'number' ? m.to.toFixed(2) : m.to.text}
                              </span>
                            ))}
                          </span>
                        )}
                        <State leg={sl} />
                      </span>
                      <span className="text-right tabular-nums text-ws-fg-2">
                        {qtyText(rs(l.lot)?.entryFilled ?? (spec.adopt ? l.qty : 0))} / {qtyText(l.qty)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* what was checked before it is offered to you, and what to keep in mind */}
          {draft(w) && !d && (checks.length > 0 || mind.length > 0) && (
            <div className="mt-8 grid gap-x-8 gap-y-8 lg:grid-cols-2">
              <div>
                <div className={head}>Risk checks</div>
                <ul className="m-0 list-none divide-y divide-ws-edge p-0">
                  {checks.map((c) => (
                    <li key={c.id} className="flex gap-3 py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-2">
                      <span className={cx('mt-[7px] block h-[6px] w-[6px] shrink-0 rounded-full', c.ok ? 'bg-ws-up' : 'bg-ws-down')} />
                      <span>
                        {c.label}
                        <span className="block text-[12px] leading-4 text-ws-fg-3">{c.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className={head}>
                  Keep in mind
                  <span className="text-[12px] tracking-normal text-ws-fg-3">does not block approval</span>
                </div>
                <ul className="m-0 list-none divide-y divide-ws-edge p-0">
                  {mind.map((x) => (
                    <li key={x} className="py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-2">
                      {x}
                    </li>
                  ))}
                  {!mind.length && <li className="py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-4">Nothing.</li>}
                </ul>
              </div>
            </div>
          )}

          {/* when it starts and how it protects — each of these can be changed by hand */}
          <div className="mt-8 grid gap-x-8 gap-y-8 lg:grid-cols-2">
            <div>
              <div className={head}>Starts when</div>
              <div className="divide-y divide-ws-edge">
                {d && entryOpen && (
                  <Line label="Entry price">
                    <input value={d.entry} onChange={(e) => set({ entry: e.target.value })} inputMode="decimal" className={field} />
                  </Line>
                )}
                {!zone && !d && <Line label="Entry">{spec.adopt ? 'already held' : spec.start.kind === 'onApproval' ? `${entryText(w)}, on approval` : `${entryText(w)}, once its start condition holds`}</Line>}
                <Line label="Buy zone" sub="It buys only while the price is inside">
                  {d && zoneOpen ? (
                    <>
                      <input value={d.lo} onChange={(e) => set({ lo: e.target.value })} inputMode="decimal" placeholder="from" className={cx(field, 'w-[72px]')} />
                      <input value={d.hi} onChange={(e) => set({ hi: e.target.value })} inputMode="decimal" placeholder="to" className={cx(field, 'w-[72px]')} />
                    </>
                  ) : zone ? (
                    `${zone.min.toFixed(2)} – ${zone.max.toFixed(2)}`
                  ) : (
                    off
                  )}
                </Line>
                {zone && (
                  <>
                    <Line label="Thesis level" sub="Below it the plan is cancelled for good">
                      {d && zoneOpen ? <input value={d.thesis} onChange={(e) => set({ thesis: e.target.value })} inputMode="decimal" placeholder="none" className={field} /> : (zone.invalid?.toFixed(2) ?? off)}
                    </Line>
                    <Line label="No-chase level" sub="Above it that day is skipped">
                      {d && zoneOpen ? <input value={d.noChase} onChange={(e) => set({ noChase: e.target.value })} inputMode="decimal" placeholder="none" className={field} /> : (zone.chase?.toFixed(2) ?? off)}
                    </Line>
                    <Line label="Sessions to watch" sub="From the start; then it gives up">
                      {d && zoneOpen ? <input value={d.sessions} onChange={(e) => set({ sessions: e.target.value })} inputMode="numeric" placeholder="as set" className={field} /> : zone.window.kind === 'sessions' ? zone.window.count : zone.window.kind === 'gtc' ? 'until stopped' : `until ${zone.window.at}`}
                    </Line>
                    <Line label="Under the zone">{d && zoneOpen ? <Choose value={d.under} options={SIDES} onChange={(v) => set({ under: v })} width={150} /> : sideText(zone.below)}</Line>
                    <Line label="Over the zone">{d && zoneOpen ? <Choose value={d.over} options={SIDES} onChange={(v) => set({ over: v })} width={150} /> : sideText(zone.above)}</Line>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className={head}>Protection</div>
              <div className="divide-y divide-ws-edge">
                <Line label="Break-even step" sub="Near a target, that lot’s stop moves to break-even, fees included">
                  {d ? <Switch on={d.breakEven} onChange={(v) => set({ breakEven: v })} label="Break-even step" /> : ins.on ? `On, at ${ins.level}% of the way` : off}
                </Line>
                <Line label="Protect the others after a profit" sub="Once one lot is sold at a gain, the other stops are pulled up">
                  {d ? <Switch on={d.protect} onChange={(v) => set({ protect: v })} label="Protect others" /> : ins.protectOthers ? 'On' : off}
                </Line>
                <Line label="Sell when it stalls near the target" sub="Takes the zone instead of waiting for the exact price">
                  {d ? <Switch on={d.stall} onChange={(v) => set({ stall: v })} label="Stall exit" /> : ins.stall?.on ? 'On' : off}
                </Line>
                {(d ? d.stall : ins.stall?.on) && (
                  <Line label="…once it is this sure" sub="That ordinary noise would have reached the target by now">
                    {d ? <Choose value={d.confidence} options={(['50', '70', '80', '90'] as const).map((c) => [c, `${c}%`] as const)} onChange={(v) => set({ confidence: v })} width={110} /> : `${ins.stall?.confidence ?? 80}%`}
                  </Line>
                )}
                <Line label="Runner" sub="The last shares do not sell at a price: they ride on with their stop">
                  {d && spec.takeProfits.length > 0 ? <Switch on={d.runner} onChange={(v) => set({ runner: v })} label="Runner" /> : runner ? 'On' : off}
                </Line>
                {d && runner && (
                  <Line label="Target for the runner" sub="A far price where even the runner is sold">
                    <input value={d.runnerTarget} onChange={(e) => set({ runnerTarget: e.target.value })} inputMode="decimal" placeholder="none" className={field} />
                  </Line>
                )}
              </div>
            </div>
          </div>

          {w.summary.rulesText.length > 0 && (
            <>
              <div className={cx(head, 'mt-8')}>
                Automatic rules
                {d && <span className="text-[12px] tracking-normal text-ws-fg-3">switch off to remove</span>}
              </div>
              <ol className="m-0 list-none divide-y divide-ws-edge p-0">
                {w.summary.rulesText.map((r) => {
                  const i = spec.rules.findIndex((x) => x.id === r.id)
                  const now = (w.marketNow ?? w.summary.marketNow)?.find((x) => x.id === r.id)?.text
                  return (
                    <li key={r.id} className={cx('grid grid-cols-[36px_1fr_auto] items-center gap-3 py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-2', d && i >= 0 && !d.keep[i] && 'text-ws-fg-4 line-through')}>
                      <span className="text-ws-fg-3">{r.id}</span>
                      <span>
                        {r.text}
                        {fired.has(r.id) && <span className="ml-2 text-[12px] text-ws-fg-4">fired</span>}
                        {now && !d && <span className="block text-[12px] leading-4 text-ws-fg-4">{now}</span>}
                      </span>
                      {d && i >= 0 && <Switch on={d.keep[i]} onChange={(v) => set({ keep: d.keep.map((x, k) => (k === i ? v : x)) })} label={`Keep rule ${r.id}`} />}
                    </li>
                  )
                })}
              </ol>
            </>
          )}

          {d && (
            <div className="mt-6 border-t border-ws-edge pt-4">
              {problems.map((x) => (
                <p key={x} className="m-0 mb-2 text-[12px] leading-[17px] tracking-normal text-ws-down">
                  {x}
                </p>
              ))}
              <div className="flex items-center gap-2">
                <Btn variant="white" disabled={busy || !diffs.length || problems.length > 0} onClick={() => void show()}>
                  {busy ? 'Working it out' : draft(w) ? 'Redraw the draft' : 'Show the change'}
                </Btn>
                <Btn variant="outline" onClick={() => useBook.setState({ editing: false })}>
                  Cancel
                </Btn>
                <span className="ml-2 text-[12px] tracking-normal text-ws-fg-3">{diffs.length ? `${diffs.length} ${diffs.length === 1 ? 'thing' : 'things'} changed` : 'Nothing changed yet'}</span>
              </div>
              {diffs.length > 0 && (
                <ul className="m-0 mt-3 list-none p-0 text-[12px] leading-[18px] tracking-normal tabular-nums text-ws-fg-3">
                  {diffs.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {w.summary.scenarios.length > 0 && !d && (live(w) || draft(w)) && (
            <>
              <div className={cx(head, 'mt-8')}>
                What happens if
                <span className="text-[12px] tracking-normal text-ws-fg-3">after fees</span>
              </div>
              <div className="divide-y divide-ws-edge">
                {w.summary.scenarios.map((x) => (
                  <div key={x.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-[10px] text-[13px] leading-5 tracking-normal">
                    <span className="min-w-0 text-ws-fg-2">
                      {x.label} <span className="text-ws-fg-3">— {x.detail}</span>
                    </span>
                    <span className={cx('tabular-nums', toneOf(x.net ?? x.pnl))}>{money(x.net ?? x.pnl)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <button type="button" aria-expanded={orders} onClick={() => setOrders(!orders)} className={cx(head, 'mt-8 w-full text-left transition-colors duration-150 hover:text-ws-fg', !orders && 'text-ws-fg-2')}>
            <ChevronRight size={13} strokeWidth={2} className={cx('-mr-1 shrink-0 text-ws-fg-4 transition-transform duration-150', orders && 'rotate-90')} />
            {draft(w) ? `These go to ${brokerOf(w)} on approval` : 'Orders of this plan'}
            <span className="text-[12px] tracking-normal text-ws-fg-3">{book.length}</span>
          </button>
          {orders && (
            <div className="ws-noscroll overflow-x-auto">
              <div className="min-w-[560px] divide-y divide-ws-edge">
                {book.map((o, i) => (
                  <div key={`${o.tag}${i}`} className="grid h-9 grid-cols-[64px_minmax(90px,0.8fr)_minmax(110px,1fr)_minmax(150px,1.2fr)_96px] items-center gap-3 text-[13px] tracking-normal tabular-nums">
                    <span className="text-ws-fg-3">{o.tag}</span>
                    <span className="text-ws-fg">{o.what}</span>
                    <span className="text-ws-fg-2">{o.kind}</span>
                    <State leg={o.state} />
                    <span className="text-right text-[12px] text-ws-fg-4">{o.id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={cx(head, 'mt-8')}>Why</div>
          <p className="m-0 mt-3 max-w-[720px] whitespace-pre-line text-[13px] leading-[20px] tracking-normal text-ws-fg-2">{spec.note || 'No note was written for this plan.'}</p>
          <div className="mt-3 flex items-center gap-2 text-[12px] tracking-normal text-ws-fg-3">
            {by && <Glyph palette={by.palette} size={14} />}
            {by ? `Minded by ${by.name} since ${day(w.approvedAt ?? w.createdAt)}` : `Since ${day(w.approvedAt ?? w.createdAt)}`}
            {w.chatId && (
              <button type="button" onClick={() => void openChat(w.chatId!)} className="ml-2 rounded-[6px] px-2 py-1 text-ws-fg-2 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg">
                Open the conversation
              </button>
            )}
          </div>
        </div>

        <div>
          <div className={head}>
            Timeline
            <span className="text-[12px] tracking-normal text-ws-fg-3">newest first</span>
          </div>
          <ol className="m-0 list-none divide-y divide-ws-edge p-0">
            {events.map((e, i) => (
              <li key={e.id} className="flex gap-3 py-[10px] text-[13px] leading-5 tracking-normal">
                <span className={cx('mt-[7px] block h-[6px] w-[6px] shrink-0 rounded-full', i === 0 ? 'bg-white' : 'bg-ws-fg-5')} />
                <span className="min-w-0">
                  <span className="block text-ws-fg-2">{e.message}</span>
                  <span className="block text-[12px] leading-4 text-ws-fg-4">{at(e.at)}</span>
                </span>
              </li>
            ))}
            {!w.events.length && <li className="py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-4">Nothing has happened yet.</li>}
          </ol>
          {w.events.length > events.length && (
            <button type="button" onClick={() => setAll(true)} className="mt-2 rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg">
              Show all {w.events.length}
            </button>
          )}
        </div>
      </div>
    </Page>
  )
}

export function WorkersView() {
  const list = useBook((s) => s.list)
  const one = useBook((s) => s.one)
  const error = useBook((s) => s.error)
  const workerId = useDemo((s) => s.workerId)
  const brokers = useDemo((s) => s.brokers)
  const go = useDemo((s) => s.go)
  const tab = useTabs((s) => s.workers)
  // The list and the open worker follow the server: every change it reports is read again.
  useEffect(() => {
    void loadList()
    void loadChats()
    let t = 0
    const off = workersApi.subscribe((changed) => {
      window.clearTimeout(t)
      t = window.setTimeout(() => void loadList(), 300)
      const open = useBook.getState()
      if (open.id && (changed === open.id || open.list?.some((w) => w.id === changed && w.revises === open.id))) void loadOne(open.id)
    })
    return () => (off(), window.clearTimeout(t))
  }, [])
  useEffect(() => {
    useBook.setState({ id: workerId, one: null, error: null })
    if (workerId) void loadOne(workerId)
  }, [workerId])
  // The price of each stock a worker is busy with (read through the server's shared cache).
  const symbols = useMemo(() => [...new Set((list ?? []).filter((w) => live(w) || draft(w) || w.id === workerId).map((w) => w.symbol))].sort().join(','), [list, workerId])
  useEffect(() => {
    if (!symbols) return
    const read = () => document.visibilityState === 'visible' && void api.quotes(symbols.split(',')).then((qs) => useBook.setState((s) => ({ prices: { ...s.prices, ...Object.fromEntries(qs.filter((q) => q.price > 0).map((q) => [q.symbol.replace(/\.US$/, ''), q.price])) } })), () => {})
    read()
    const t = window.setInterval(read, 60_000)
    return () => window.clearInterval(t)
  }, [symbols])

  if (workerId) {
    if (one?.id === workerId) return <Detail w={one} change={list?.find((w) => w.revises === one.id && w.status === 'draft')} />
    return <Page className="mx-auto max-w-[1360px]">{error ? <p className="m-0 text-[13px] tracking-normal text-ws-fg-3">{error}</p> : <div className="h-40 animate-ws-pulse rounded-[10px] bg-ws-panel" />}</Page>
  }
  if (!list) return <Page className="mx-auto max-w-[1360px]">{error ? <p className="m-0 text-[13px] tracking-normal text-ws-fg-3">{error}</p> : <div className="h-24 animate-ws-pulse rounded-[10px] bg-ws-panel" />}</Page>
  if (!brokers || (!list.length && ['running', 'drafts', 'history'].includes(tab)))
    return (
      <Empty
        icon={<Steps />}
        title={brokers ? 'No workers yet' : 'Workers need a broker'}
        action={
          !brokers && (
            <Btn variant="white" className="px-3" onClick={() => go('apps')}>
              Connect a broker
            </Btn>
          )
        }
      >
        {brokers ? 'Ask an agent for a plan. Once you approve it, a worker carries it out and it shows up here.' : 'A worker carries out a plan you approved: it buys, protects and sells at a broker. This workspace has none connected.'}
      </Empty>
    )
  return <List workers={list} />
}
